import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Search, ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";

export default function WarrantyApprovalsPage() {
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [rejectFor, setRejectFor] = useState(null); // record | "bulk"
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    const r = await api.get("/records", { params: { request_type: "warranty" } });
    setRecords(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (statusFilter !== "__all" && (r.warranty_approval_status || "pending") !== statusFilter) return false;
      if (q) {
        const term = q.toLowerCase();
        const d = r.data || {};
        const blob = [r.ticket_id, d.full_name, d.phone, d.email, d.serial_number, d.unique_product_id]
          .filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [records, statusFilter, q]);

  const approve = async (rec) => {
    setBusy(true);
    try {
      await api.post(`/records/${rec.id}/approve_warranty`, { approved: true });
      toast.success(`Approved ${rec.ticket_id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const rejectSubmit = async () => {
    setBusy(true);
    try {
      if (rejectFor === "bulk") {
        const ids = Array.from(selected);
        const r = await api.post("/records/bulk_approve_warranty", { record_ids: ids, approved: false, reason: rejectReason });
        toast.success(`Rejected ${r.data.updated} record${r.data.updated === 1 ? "" : "s"}`);
        setSelected(new Set());
      } else {
        await api.post(`/records/${rejectFor.id}/approve_warranty`, { approved: false, reason: rejectReason });
        toast.success(`Rejected ${rejectFor.ticket_id}`);
      }
      setRejectFor(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (id) => setSelected((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const toggleAll = () => setSelected((prev) => {
    if (prev.size === filtered.length) return new Set();
    return new Set(filtered.map((r) => r.id));
  });
  const clearSelection = () => setSelected(new Set());

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.post("/records/bulk_approve_warranty", { record_ids: ids, approved: true });
      toast.success(`Approved ${r.data.updated} record${r.data.updated === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <div className="data-label mb-1">Warranty approvals</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">Warranty Team</h1>
        <p className="text-sm text-[#71717A] mt-1 max-w-2xl">
          Approve or reject product registrations from your side. The Sales Incentives team approves incentive payouts separately.
        </p>
      </div>

      <div className="card-shell p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            className="pl-9"
            placeholder="Search by ticket, customer, serial…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="warranty-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="warranty-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="__all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="card-shell p-3 flex items-center justify-between gap-3 bg-[#F0FDF4] border-[#86EFAC]" data-testid="warranty-bulk-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> record{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={clearSelection} data-testid="warranty-clear-selection">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setRejectFor("bulk"); setRejectReason(""); }} data-testid="warranty-bulk-reject">
              <XCircle className="w-4 h-4 mr-1" /> Reject selected
            </Button>
            <Button size="sm" className="bg-[#059669] hover:bg-[#047857] text-white" onClick={bulkApprove} disabled={busy} data-testid="warranty-bulk-approve">
              <ShieldCheck className="w-4 h-4 mr-1" /> Approve selected
            </Button>
          </div>
        </div>
      )}

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Checkbox
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={toggleAll}
                    data-testid="warranty-select-all"
                  />
                </th>
                <th>Ticket</th><th>Customer</th><th>Product Model</th><th>Serial</th>
                <th>Unique ID</th><th>Warranty status</th><th></th>
              </tr>
            </thead>
            <tbody data-testid="warranty-tbody">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-[#71717A] text-sm">Nothing to review.</td></tr>
              ) : filtered.map((r) => {
                const d = r.data || {};
                const s = r.warranty_approval_status || "pending";
                return (
                  <tr key={r.id} data-testid={`warranty-row-${r.ticket_id}`}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggleRow(r.id)}
                        data-testid={`warranty-check-${r.ticket_id}`}
                      />
                    </td>
                    <td className="font-mono text-xs font-medium">
                      <Link to={`/records/${r.id}`} className="text-[#4F46E5] hover:underline">{r.ticket_id}</Link>
                    </td>
                    <td>
                      <div className="font-medium">{d.full_name || "—"}</div>
                      <div className="text-xs text-[#71717A]">{d.phone || d.email || ""}</div>
                    </td>
                    <td className="text-sm">{d.product_model || "—"}</td>
                    <td className="font-mono text-xs">{d.serial_number || "—"}</td>
                    <td className="font-mono text-xs">{d.unique_product_id || "—"}</td>
                    <td>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono ${
                        s === "approved" ? "bg-[#DCFCE7] text-[#166534]" :
                        s === "rejected" ? "bg-[#FEE2E2] text-[#991B1B]" :
                        "bg-[#FEF3C7] text-[#92400E]"
                      }`}>{s}</span>
                    </td>
                    <td className="whitespace-nowrap">
                      {s === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="bg-[#059669] hover:bg-[#047857] text-white mr-1"
                            onClick={() => approve(r)}
                            disabled={busy}
                            data-testid={`warranty-approve-${r.ticket_id}`}
                          >
                            <ShieldCheck className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setRejectFor(r); setRejectReason(""); }}
                            data-testid={`warranty-reject-${r.ticket_id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {s === "rejected" && r.warranty_rejection_reason && (
                        <div className="text-xs text-[#DC2626]" title={r.warranty_rejection_reason}>
                          {r.warranty_rejection_reason.slice(0, 30)}{r.warranty_rejection_reason.length > 30 ? "…" : ""}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rejectFor === "bulk" ? `Reject ${selected.size} record${selected.size === 1 ? "" : "s"}` : `Reject ${rejectFor?.ticket_id}`}
            </DialogTitle>
            <DialogDescription>The customer team will see this reason internally.</DialogDescription>
          </DialogHeader>
          <Label className="data-label mb-1.5 block">Reason</Label>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. invoice mismatch / serial not on record"
            data-testid="warranty-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={rejectSubmit} disabled={busy} data-testid="warranty-reject-confirm">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
